import { useState, useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useNavigate, Link } from "react-router-dom";
import "./index.css";
import { AuthProvider, useAuth } from "./context/AuthContext";
import Navbar from "./components/Navbar";
import Home from "./pages/Home";
import MoviePage from "./pages/MoviePage";
import Discussions from "./pages/Discussions";
import Lists from "./pages/Lists";
import ShowDetail from "./pages/ShowDetail";
import Auth from "./pages/Auth";
import ProfilePage from "./pages/ProfilePage";
import SettingsPage from "./pages/SettingsPage";
import SearchPage from "./pages/SearchPage";
import AdminPage from "./pages/AdminPage";
import FeedbackPage from "./pages/FeedbackPage";

function AuthGateModal() {
  const { authGate, closeAuthGate } = useAuth();
  const navigate = useNavigate();
  if (!authGate.visible) return null;

  return (
    <div
      onClick={closeAuthGate}
      style={{
        position: "fixed", inset: 0, zIndex: 9000,
        background: "rgba(0,0,0,0.72)",
        display: "flex", alignItems: "center", justifyContent: "center",
        backdropFilter: "blur(4px)",
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          background: "#111113", border: "0.5px solid #2a2a2e", borderRadius: 16,
          padding: "32px 28px", maxWidth: 340, width: "100%", textAlign: "center",
        }}
      >
        <div style={{ fontSize: 32, marginBottom: 12 }}>🎬</div>
        <div style={{
          fontFamily: "'Bricolage Grotesque', sans-serif",
          fontSize: 17, fontWeight: 800, color: "#f0f0f0",
          marginBottom: 8,
        }}>
          Join the guff to {authGate.action}
        </div>
        <div style={{
          fontSize: 12, color: "#55555e", marginBottom: 22,
          fontFamily: "'Plus Jakarta Sans', sans-serif", lineHeight: 1.6,
        }}>
          Sign up free — verify with phone or email in 30 seconds.
        </div>
        <button
          onClick={() => { closeAuthGate(); navigate("/auth"); }}
          style={{
            width: "100%", padding: "11px", borderRadius: 10, border: "none",
            background: "#FF5C35", color: "#fff", fontSize: 13, fontWeight: 700,
            fontFamily: "'Bricolage Grotesque', sans-serif", cursor: "pointer",
            marginBottom: 8,
          }}
        >
          Join GuffGaff
        </button>
        <button
          onClick={closeAuthGate}
          style={{
            width: "100%", padding: "11px", borderRadius: 10,
            border: "0.5px solid #252528", background: "transparent",
            color: "#55555e", fontSize: 12, cursor: "pointer",
            fontFamily: "'Plus Jakarta Sans', sans-serif",
          }}
        >
          Maybe later
        </button>
      </div>
    </div>
  );
}

function PrivateRoute({ children }) {
  const { isLoggedIn, loading } = useAuth();
  if (loading) return null;
  return isLoggedIn ? children : <Navigate to="/auth" replace />;
}

function AdminRoute({ children }) {
  const { isLoggedIn, currentUser, loading } = useAuth();
  if (loading) return null;
  if (!isLoggedIn) return <Navigate to="/auth" replace />;
  if (!currentUser?.is_admin) return <Navigate to="/" replace />;
  return children;
}

function BetaBanner() {
  const [visible, setVisible] = useState(
    () => sessionStorage.getItem("betaBannerDismissed") !== "1"
  );

  useEffect(() => {
    if (!visible) sessionStorage.setItem("betaBannerDismissed", "1");
  }, [visible]);

  if (!visible) return null;

  return (
    <div style={{
      position: "sticky", top: 0, zIndex: 110,
      background: "linear-gradient(90deg, #1a0e08, #1e1008, #1a0e08)",
      borderBottom: "0.5px solid rgba(255,92,53,0.2)",
      padding: "7px 16px",
      display: "flex", alignItems: "center", justifyContent: "center", gap: 10,
    }}>
      <span style={{ fontSize: 12 }}>🚧</span>
      <span style={{
        fontSize: 12, color: "#c0804a",
        fontFamily: "'Plus Jakarta Sans', sans-serif",
        lineHeight: 1,
      }}>
        GuffGaff is in beta — we're still building!{" "}
        <Link to="/feedback" style={{ color: "#FF5C35", textDecoration: "underline", textUnderlineOffset: 2 }}>
          Your feedback helps us improve.
        </Link>
      </span>
      <button
        onClick={() => setVisible(false)}
        aria-label="Dismiss"
        style={{
          position: "absolute", right: 12,
          background: "transparent", border: "none", cursor: "pointer",
          color: "#6a4a32", fontSize: 15, lineHeight: 1, padding: "2px 4px",
          transition: "color 0.1s",
        }}
        onMouseEnter={e => { e.currentTarget.style.color = "#FF5C35"; }}
        onMouseLeave={e => { e.currentTarget.style.color = "#6a4a32"; }}
      >✕</button>
    </div>
  );
}

function AppInner() {
  return (
    <div style={{ minHeight: "100vh", background: "#0b0b0c" }}>
      <BetaBanner />
      <Navbar />
      <AuthGateModal />
      <Routes>
        <Route path="/"            element={<Home />} />
        <Route path="/movie/:id"   element={<MoviePage />} />
        <Route path="/show/:id"    element={<ShowDetail />} />
        <Route path="/auth"        element={<Auth />} />
        <Route path="/discussions" element={<PrivateRoute><Discussions /></PrivateRoute>} />
        <Route path="/lists"       element={<PrivateRoute><Lists /></PrivateRoute>} />
        <Route path="/profile"     element={<PrivateRoute><ProfilePage /></PrivateRoute>} />
        <Route path="/settings"    element={<PrivateRoute><SettingsPage /></PrivateRoute>} />
        <Route path="/search"      element={<SearchPage />} />
        <Route path="/feedback"    element={<FeedbackPage />} />
        <Route path="/admin"       element={<AdminRoute><AdminPage /></AdminRoute>} />
        <Route path="*"            element={<Home />} />
      </Routes>
    </div>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppInner />
      </AuthProvider>
    </BrowserRouter>
  );
}
